import React, { createContext, useContext, useEffect } from 'react';
import {
  getCurrentUserInfo,
  getOrganizationAccessIcons,
} from '../../../services/userService.js';
import { Context } from '../../../store/Store.js';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import {
  NO_BUDGET,
  SHOW_COLUMNS_DEFAULT_VALUES,
} from '../../../static/constants.js';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from '../../../static/localStorageStrings.js';
import { REDUCER_ACTION_TYPES } from '../../../store/Reducer.js';
import { useUser } from '../../../contexts/userContext.js';
import { useLocation, useNavigate } from 'react-router';
import { NAVIGATION_PATHS } from '../../../static/navigationPaths.js';
import { setActiveOrganization } from '../../../services/organizationService.js';
import { getCostModel } from '../../../services/costModelService.js';
import { changeBudget } from '../../OmniBarNavigation/NavDrawer/BudgetManager.js';
import { useNameConventions } from '../../../contexts/nameConventionsContext.js';
import { LoaderContext } from '../../../contexts/loaderContext.js';
import useActiveBudgetProgramCount from '../../../customHooks/useActiveBudgetProgramCount.js';

const SessionContext = createContext();

export const useSession = () => useContext(SessionContext);

const PUBLIC_ROUTES = [
  NAVIGATION_PATHS.LOGIN,
  NAVIGATION_PATHS.LOGOUT,
  NAVIGATION_PATHS.SESSION_TIMEOUT,
  NAVIGATION_PATHS.SESSION_ENDED,
  NAVIGATION_PATHS.UNAUTHORISED_ACCESS,
  `/${NAVIGATION_PATHS.INVALID_URL}`,
];

const SessionChecker = ({ children }) => {
  const [state, dispatch] = useContext(Context);
  const {
    user,
    setUser,
    setShowColumns,
    setResultTypes,
    costModel,
    setCostModel,
    lastPath,
    initSelectedBudget,
    setInitSelectedBudget,
    setOrganizationAccessIcons,
    setOrganizationBudgets,
  } = useUser();
  const { setNameConventions } = useNameConventions();
  const { setLoading } = useContext(LoaderContext);

  const location = useLocation();
  const navigate = useNavigate();
  useActiveBudgetProgramCount(user, dispatch);

  const params = new URLSearchParams(location.search);
  const isEmailMismatch = params.get('ssoEmailMismatch') === 'true';

  useEffect(() => {
    const checkSession = async () => {
      setLoading(true);

      try {
        if (PUBLIC_ROUTES.includes(location.pathname)) {
          // reloading /login drops the in-memory user but the cookie may still
          // be valid - if so, send them home instead of the login form
          if (
            location.pathname === NAVIGATION_PATHS.LOGIN &&
            !isEmailMismatch
          ) {
            try {
              const response = await getCurrentUserInfo();
              const userObj = response?.data?.user;

              if (userObj?.email) {
                await loadExistingData(userObj, true);
                dispatch({
                  type: REDUCER_ACTION_TYPES.SET_SESSION_CHECKED,
                  payload: true,
                });
                return;
              }
            } catch (error) {
              // no active session - fall through and show the login page
            }
          }

          dispatch({
            type: REDUCER_ACTION_TYPES.SET_SESSION_CHECKED,
            payload: true,
          });
          setLoading(false);
          return;
        }

        if (location.pathname === '/resetPassword') {
          navigate('/resetPassword', {
            state: { token: params.get('token') },
          });
          dispatch({
            type: REDUCER_ACTION_TYPES.SET_SESSION_CHECKED,
            payload: true,
          });
          setLoading(false);
          return;
        }

        if (user && user.email) {
          dispatch({
            type: REDUCER_ACTION_TYPES.SET_SESSION_CHECKED,
            payload: true,
          });
          return;
        }
        const response = await getCurrentUserInfo();
        const userObj = response?.data?.user;
        dispatch({
          type: REDUCER_ACTION_TYPES.SET_SESSION_CHECKED,
          payload: true,
        });
        loadExistingData(userObj);
      } catch (error) {
        dispatch({
          type: REDUCER_ACTION_TYPES.SET_SESSION_CHECKED,
          payload: true,
        });
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === null) {
        localStorage.clear();
        ReactGA.set({ dimension1: null, dimension2: null });
        ReactGA.pageview(window.location.pathname + window.location.hash, {
          dimension1: null,
          dimension2: null,
        });
        navigate(NAVIGATION_PATHS.SESSION_TIMEOUT, {
          state: { manualLogout: true },
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const populateCostModel = async () => {
    if (!costModel) {
      const resp = (await getCostModel())?.data;
      setCostModel(resp);
    }
  };

  const populateActiveBudget = async (user) => {
    if (
      !initSelectedBudget &&
      user?.organizationBudgets &&
      user.organizationBudgets.length > 0
    ) {
      const budgetFromSession = user.organizationBudgets.find(
        (budget) => budget.id == user?.activeBudget?.id
      );

      if (budgetFromSession !== undefined) {
        const updatedUser = {
          ...user,
          activeBudget: budgetFromSession,
        };

        setUser(updatedUser);
        setInitSelectedBudget(budgetFromSession);

      } else {
        const budget = user?.organizationBudgets[0];
        changeBudget(
          budget,
          user,
          setUser,
          () => {
            setInitSelectedBudget(budget);
          }
        );
      }
    }
  };

  const populateOrganizationIcons = async () => {
    getOrganizationAccessIcons().then((res) => {
      setOrganizationAccessIcons(res.data);
      dispatch({
        type: REDUCER_ACTION_TYPES.REFRESH_ORGANIZATION_ACCESS,
        payload: false,
      });
    });
  };

  const getOrganizationName = (urlOrganization, organizations) => {
    const decodedUrlOrg =
      urlOrganization ? decodeURIComponent(urlOrganization) : null;

    if (decodedUrlOrg && organizations.includes(decodedUrlOrg))
      return decodedUrlOrg;
    if (decodedUrlOrg && !organizations.includes(decodedUrlOrg)) return null;
    return organizations[0];
  };

  const loadExistingData = async (userObj, ignoreUrlPath = false) => {
    if (userObj) {
      // no org/budget in the url on /login, so use the user's default org
      const organization = getOrganizationName(
        ignoreUrlPath ? null : lastPath?.orgId,
        userObj.organizationNames,
      );

      if (!organization) {
        navigate(NAVIGATION_PATHS.INVALID_URL);
        setLoading(false);
        return;
      }

      try {
        const res = await setActiveOrganization(organization);
        const backendUser = { ...userObj, ...res.data };

        const storedActiveCostCenters =
          sessionStorage.getItem(SESSION_STORAGE_KEYS.ACTIVE_COST_CENTERS);
        let activeCostCenters;

        if (storedActiveCostCenters) {
          activeCostCenters = JSON.parse(storedActiveCostCenters);
        } else {
          activeCostCenters = backendUser.activeCostCenters;
          sessionStorage.setItem(
            SESSION_STORAGE_KEYS.ACTIVE_COST_CENTERS,
            JSON.stringify(activeCostCenters),
          );
        }
        const newActiveCostCenterIds = backendUser.rawCostCenterArray.reduce(
          (acc, cc) => {
            if (activeCostCenters.includes(cc.name)) {
              acc.push(cc.id);
            }
            return acc;
          },
          [],
        );

        const _user = {
          ...backendUser,
          activeCostCenters: activeCostCenters,
          activeCostCenterIds: newActiveCostCenterIds,
        };

        const urlBudgetId = ignoreUrlPath ? null : lastPath?.budgetId;
        const availableBudgets = _user?.organizationBudgets || [];

        let finalBudgetId = null;

        if (
          urlBudgetId &&
          urlBudgetId !== 'undefined' &&
          urlBudgetId !== NO_BUDGET
        ) {
          const isBudgetValid = availableBudgets.some(
            (budget) => budget.id == urlBudgetId,
          );

          if (!isBudgetValid) {
            navigate(NAVIGATION_PATHS.INVALID_URL);
            setLoading(false);
            return;
          }
          finalBudgetId = urlBudgetId;
        } else {
          finalBudgetId =
            _user?.activeBudget?.id ||
            (availableBudgets[0] ? availableBudgets[0].id : NO_BUDGET);
        }

        await populateCostModel();
        await populateOrganizationIcons();
        populateActiveBudget(_user);
        setOrganizationBudgets(availableBudgets);
        setUser(_user);
        setNameConventions(_user?.nameConventions);

        window.localStorage.setItem(
          LOCAL_STORAGE_KEYS.DEFAULT_THEME,
          JSON.stringify(_user?.defaultTheme),
        );
        setShowColumns(
          _user?.showColumns ? _user?.showColumns : SHOW_COLUMNS_DEFAULT_VALUES,
        );
        setResultTypes(_user?.resultTypes);

        if (location.pathname === '/' || !urlBudgetId) {
          navigate(`/${organization}/${finalBudgetId}`);
        }

        dispatch({
          type: REDUCER_ACTION_TYPES.SET_SESSION_CHECKED,
          payload: true,
        });
      } finally {

        setLoading(false);
      }
    }
  };

  return (
    <SessionContext.Provider value={{}}>
      {state.sessionChecked && children}
    </SessionContext.Provider>
  );
};

SessionChecker.propTypes = {
  children: PropTypes.node,
};

export default SessionChecker;
